import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { babyApi, inviteApi, emergencyApi, shareApi, photoApi } from '../services/api';
import {
  generateAESKey,
  encryptBabyProfile,
  decryptBabyProfile,
  getBabyKey,
  storeBabyKey,
  encryptKeyWithPublicKey,
  importPublicKey,
  exportAESKey,
  encryptBinary,
  createDecryptedImageUrl,
  encryptBabyKeyForInvite,
} from '../crypto';
import { useAuth } from '../hooks/useAuth';
import { format } from 'date-fns';
import type { BabyProfileData } from '../crypto';

interface Invite {
  id: string;
  email: string | null;
  role: string;
  token: string;
  expiresAt: string;
  usedAt: string | null;
}

interface Member {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface EmergencyContact {
  id: string;
  email: string;
  name: string;
  relationship: string;
  isActive: boolean;
  requests?: Array<{
    id: string;
    status: string;
    createdAt: string;
  }>;
}

interface ShareLink {
  id: string;
  token: string;
  eventTypes: string[] | null;
  dateFrom: string | null;
  dateTo: string | null;
  expiresAt: string;
  accessCount: number;
  maxAccessCount: number | null;
  revoked: boolean;
  recipientEmail: string | null;
  recipientName: string | null;
  createdAt: string;
}

export default function BabyProfile() {
  const { babyId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isNew = !babyId;

  // Form state
  const [name, setName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState<'male' | 'female' | 'other' | ''>('');
  const [bloodType, setBloodType] = useState('');
  const [allergies, setAllergies] = useState('');
  const [medications, setMedications] = useState('');
  const [notes, setNotes] = useState('');

  // Feed target range
  const [dailyFeedTargetMinMl, setDailyFeedTargetMinMl] = useState('');
  const [dailyFeedTargetMaxMl, setDailyFeedTargetMaxMl] = useState('');
  const [showWeightCalculator, setShowWeightCalculator] = useState(false);
  const [babyWeightKg, setBabyWeightKg] = useState('');

  // Post-feed upright timer
  const [postFeedUprightMinutes, setPostFeedUprightMinutes] = useState('');

  // Pediatrician
  const [pedName, setPedName] = useState('');
  const [pedPhone, setPedPhone] = useState('');
  const [pedAddress, setPedAddress] = useState('');

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Invite state
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'MEMBER'>('MEMBER');
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [isInviting, setIsInviting] = useState(false);

  // Profile photo state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  // Emergency contact state
  const [showEmergencyForm, setShowEmergencyForm] = useState(false);
  const [emergencyName, setEmergencyName] = useState('');
  const [emergencyEmail, setEmergencyEmail] = useState('');
  const [emergencyRelationship, setEmergencyRelationship] = useState('');
  const [isAddingEmergency, setIsAddingEmergency] = useState(false);

  // Share link state
  const [showShareForm, setShowShareForm] = useState(false);
  const [shareEventTypes, setShareEventTypes] = useState<string[]>([]);
  const [shareExpiresInDays, setShareExpiresInDays] = useState(7);
  const [shareRecipientName, setShareRecipientName] = useState('');
  const [shareRecipientEmail, setShareRecipientEmail] = useState('');
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [isCreatingShare, setIsCreatingShare] = useState(false);

  // Fetch existing baby data
  const { data: babyData, isLoading } = useQuery({
    queryKey: ['baby', babyId],
    queryFn: async () => {
      if (isNew || !babyId) return null;
      const response = await babyApi.getById(babyId);
      return response.data;
    },
    enabled: !isNew && !!babyId,
  });

  // Fetch members (caregivers) for this baby
  const { data: members } = useQuery({
    queryKey: ['baby-members', babyId],
    queryFn: async () => {
      if (isNew || !babyId) return [];
      const response = await babyApi.getMembers(babyId);
      return response.data as Member[];
    },
    enabled: !isNew && !!babyId,
  });

  // Fetch pending invites
  const { data: invites, refetch: refetchInvites } = useQuery({
    queryKey: ['baby-invites', babyId],
    queryFn: async () => {
      if (isNew || !babyId) return [];
      const response = await inviteApi.listForBaby(babyId);
      return response.data as Invite[];
    },
    enabled: !isNew && !!babyId && babyData?.role === 'PRIMARY',
  });

  // Fetch emergency contacts
  const { data: emergencyContacts, refetch: refetchEmergencyContacts } = useQuery({
    queryKey: ['emergency-contacts', babyId],
    queryFn: async () => {
      if (isNew || !babyId) return [];
      const response = await emergencyApi.listContacts(babyId);
      return response.data as EmergencyContact[];
    },
    enabled: !isNew && !!babyId && babyData?.role === 'PRIMARY',
  });

  // Fetch share links
  const { data: shareLinks, refetch: refetchShareLinks } = useQuery({
    queryKey: ['share-links', babyId],
    queryFn: async () => {
      if (isNew || !babyId) return [];
      const response = await shareApi.listForBaby(babyId);
      return response.data as ShareLink[];
    },
    enabled: !isNew && !!babyId && babyData?.role === 'PRIMARY',
  });

  // Decrypt and populate form when data loads
  useEffect(() => {
    async function loadBabyData() {
      if (!babyData || isNew) return;

      try {
        const aesKey = await getBabyKey(babyData.id);
        if (!aesKey) {
          setError('Could not decrypt baby profile. Key not found.');
          return;
        }

        const decrypted = await decryptBabyProfile(babyData.encryptedData, aesKey);

        setName(decrypted.name);
        setDateOfBirth(decrypted.dateOfBirth || '');
        setGender(decrypted.gender || '');
        setBloodType(decrypted.bloodType || '');
        setAllergies(decrypted.allergies?.join(', ') || '');
        setMedications(decrypted.medications?.join(', ') || '');
        setNotes(decrypted.notes || '');
        // Migrate old single target to range if needed
        if (decrypted.dailyFeedTargetMinMl) {
          setDailyFeedTargetMinMl(String(decrypted.dailyFeedTargetMinMl));
        } else if (decrypted.dailyFeedTargetMl) {
          setDailyFeedTargetMinMl(String(decrypted.dailyFeedTargetMl));
        }
        if (decrypted.dailyFeedTargetMaxMl) {
          setDailyFeedTargetMaxMl(String(decrypted.dailyFeedTargetMaxMl));
        } else if (decrypted.dailyFeedTargetMl) {
          setDailyFeedTargetMaxMl(String(decrypted.dailyFeedTargetMl));
        }
        setPostFeedUprightMinutes(decrypted.postFeedUprightMinutes ? String(decrypted.postFeedUprightMinutes) : '');
        setPedName(decrypted.pediatrician?.name || '');
        setPedPhone(decrypted.pediatrician?.phone || '');
        setPedAddress(decrypted.pediatrician?.address || '');

        // Load profile photo if exists
        if (babyData.profilePhotoId) {
          try {
            // Download through backend to avoid CORS issues
            const { blob: encryptedBlob, mimeType } = await photoApi.downloadDirect(
              babyData.profilePhotoId
            );

            // Decrypt and create URL
            const imageUrl = await createDecryptedImageUrl(
              encryptedBlob,
              aesKey,
              mimeType
            );
            setProfilePhotoUrl(imageUrl);
          } catch (photoErr) {
            console.error('Failed to load profile photo:', photoErr);
          }
        }
      } catch (err) {
        console.error('Failed to decrypt baby data:', err);
        setError('Failed to decrypt baby profile');
      }
    }

    loadBabyData();
  }, [babyData, isNew]);

  // Clean up photo URL on unmount
  useEffect(() => {
    return () => {
      if (profilePhotoUrl) {
        URL.revokeObjectURL(profilePhotoUrl);
      }
    };
  }, [profilePhotoUrl]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: { encryptedData: string; encryptedBabyKey: string; profileType: string }) => {
      const response = await babyApi.create(data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['babies'] });
      navigate(`/baby/${data.id}`);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, encryptedData }: { id: string; encryptedData: string }) => {
      const response = await babyApi.update(id, { encryptedData });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['babies'] });
      queryClient.invalidateQueries({ queryKey: ['baby', babyId] });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await babyApi.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['babies'] });
      navigate('/app/dashboard');
    },
  });

  // Remove member mutation
  const removeMemberMutation = useMutation({
    mutationFn: async ({ odId, userId }: { odId: string; userId: string }) => {
      await babyApi.removeMember(odId, userId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['baby-members', babyId] });
    },
  });

  // Delete invite mutation
  const deleteInviteMutation = useMutation({
    mutationFn: async (inviteId: string) => {
      await inviteApi.delete(inviteId);
    },
    onSuccess: () => {
      refetchInvites();
    },
  });

  // Remove emergency contact mutation
  const removeEmergencyContactMutation = useMutation({
    mutationFn: async (contactId: string) => {
      await emergencyApi.removeContact(contactId);
    },
    onSuccess: () => {
      refetchEmergencyContacts();
    },
  });

  // Approve emergency request mutation
  const approveEmergencyRequestMutation = useMutation({
    mutationFn: async (requestId: string) => {
      await emergencyApi.approveRequest(requestId);
    },
    onSuccess: () => {
      refetchEmergencyContacts();
    },
  });

  // Deny emergency request mutation
  const denyEmergencyRequestMutation = useMutation({
    mutationFn: async (requestId: string) => {
      await emergencyApi.denyRequest(requestId);
    },
    onSuccess: () => {
      refetchEmergencyContacts();
    },
  });

  // Revoke share link mutation
  const revokeShareMutation = useMutation({
    mutationFn: async (shareId: string) => {
      await shareApi.revoke(shareId);
    },
    onSuccess: () => {
      refetchShareLinks();
    },
  });

  // Handle invite creation
  const handleCreateInvite = async () => {
    if (!babyId) return;

    setIsInviting(true);
    setError(null);

    try {
      // Get the baby's AES key
      const babyAESKey = await getBabyKey(babyId);
      if (!babyAESKey) {
        throw new Error('Unable to access baby encryption key');
      }

      // Generate a 64-char hex token (32 bytes = 64 hex chars)
      const tokenBytes = new Uint8Array(32);
      crypto.getRandomValues(tokenBytes);
      const token = Array.from(tokenBytes).map(b => b.toString(16).padStart(2, '0')).join('');

      // Encrypt the baby key with a key derived from the token
      const encryptedBabyKey = await encryptBabyKeyForInvite(babyAESKey, token);

      const response = await inviteApi.create(babyId, {
        email: inviteEmail || undefined,
        role: inviteRole,
        expiresInDays: 7,
        encryptedBabyKey,
        token,
      });

      // Generate the invite link
      const baseUrl = window.location.origin;
      const link = `${baseUrl}/invite/${response.data.token}`;
      setInviteLink(link);

      // Clear form
      setInviteEmail('');
      refetchInvites();
    } catch (err) {
      setError((err as Error).message || 'Failed to create invite');
    } finally {
      setIsInviting(false);
    }
  };

  // Copy invite link to clipboard
  const copyInviteLink = async () => {
    if (inviteLink) {
      await navigator.clipboard.writeText(inviteLink);
      alert('Invite link copied to clipboard!');
    }
  };

  // Handle profile photo upload
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !babyId || isNew) return;

    // Validate file type and size
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    setIsUploadingPhoto(true);
    setError(null);

    try {
      const aesKey = await getBabyKey(babyId);
      if (!aesKey) {
        throw new Error('Could not find encryption key');
      }

      // Read file as ArrayBuffer
      const fileBuffer = await file.arrayBuffer();

      // Encrypt the image
      const encryptedData = await encryptBinary(fileBuffer, aesKey);

      // Export the key for storage
      const exportedKey = await exportAESKey(aesKey);

      // Upload through the backend (avoids CORS issues with R2)
      const uploadResponse = await photoApi.uploadDirect(
        babyId,
        {
          mimeType: file.type,
          encryptedKey: exportedKey,
        },
        encryptedData
      );

      if (!uploadResponse.data?.photoId) {
        throw new Error('Failed to upload photo');
      }

      // Update baby's profile photo reference
      await babyApi.updateProfilePhoto(babyId, uploadResponse.data.photoId);

      // Create local preview URL from original file (not encrypted)
      const imageUrl = URL.createObjectURL(file);

      // Revoke old URL if exists
      if (profilePhotoUrl) {
        URL.revokeObjectURL(profilePhotoUrl);
      }

      setProfilePhotoUrl(imageUrl);

      // Refresh baby data
      queryClient.invalidateQueries({ queryKey: ['baby', babyId] });
    } catch (err) {
      console.error('Failed to upload photo:', err);
      setError((err as Error).message || 'Failed to upload photo');
    } finally {
      setIsUploadingPhoto(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Remove profile photo
  const handleRemovePhoto = async () => {
    if (!babyId || isNew) return;

    try {
      await babyApi.updateProfilePhoto(babyId, null);

      if (profilePhotoUrl) {
        URL.revokeObjectURL(profilePhotoUrl);
      }
      setProfilePhotoUrl(null);

      queryClient.invalidateQueries({ queryKey: ['baby', babyId] });
    } catch (err) {
      setError((err as Error).message || 'Failed to remove photo');
    }
  };

  // Handle adding emergency contact
  const handleAddEmergencyContact = async () => {
    if (!babyId || !emergencyName.trim() || !emergencyEmail.trim() || !emergencyRelationship.trim()) {
      setError('Please fill in all emergency contact fields');
      return;
    }

    setIsAddingEmergency(true);
    setError(null);

    try {
      // Get the baby's AES key to decrypt profile data
      const aesKey = await getBabyKey(babyId);
      if (!aesKey) {
        throw new Error('Could not find encryption key');
      }

      // Decrypt the profile to get emergency-relevant data
      const profile = await decryptBabyProfile(babyData.encryptedData, aesKey);

      // Create an emergency access packet with essential medical info
      const emergencyPacket = {
        babyName: profile.name,
        allergies: profile.allergies,
        medications: profile.medications,
        bloodType: profile.bloodType,
        pediatrician: profile.pediatrician,
        emergencyNotes: profile.notes,
      };

      // Encode the packet (in a real implementation, this would be encrypted with the contact's key)
      const encryptedAccessPacket = btoa(JSON.stringify(emergencyPacket));

      await emergencyApi.addContact(babyId, {
        email: emergencyEmail.trim(),
        name: emergencyName.trim(),
        relationship: emergencyRelationship.trim(),
        encryptedAccessPacket,
      });

      // Clear form and close
      setEmergencyName('');
      setEmergencyEmail('');
      setEmergencyRelationship('');
      setShowEmergencyForm(false);
      refetchEmergencyContacts();
    } catch (err) {
      setError((err as Error).message || 'Failed to add emergency contact');
    } finally {
      setIsAddingEmergency(false);
    }
  };

  // Handle creating share link
  const handleCreateShareLink = async () => {
    if (!babyId) return;

    setIsCreatingShare(true);
    setError(null);

    try {
      // Get the baby's AES key
      const aesKey = await getBabyKey(babyId);
      if (!aesKey) {
        throw new Error('Could not find encryption key');
      }

      // Export the key for sharing
      const exportedKey = await exportAESKey(aesKey);

      const response = await shareApi.create(babyId, {
        encryptedKey: exportedKey,
        eventTypes: shareEventTypes.length > 0 ? shareEventTypes : undefined,
        expiresInDays: shareExpiresInDays,
        recipientName: shareRecipientName.trim() || undefined,
        recipientEmail: shareRecipientEmail.trim() || undefined,
      });

      // Generate the share link
      const baseUrl = window.location.origin;
      const link = `${baseUrl}/share/${response.data.token}`;
      setShareLink(link);

      refetchShareLinks();
    } catch (err) {
      setError((err as Error).message || 'Failed to create share link');
    } finally {
      setIsCreatingShare(false);
    }
  };

  // Copy share link to clipboard
  const copyShareLink = async () => {
    if (shareLink) {
      await navigator.clipboard.writeText(shareLink);
      alert('Share link copied to clipboard!');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      // Validate required fields
      if (!name.trim()) {
        setError('Name is required');
        setIsSubmitting(false);
        return;
      }

      if (!dateOfBirth) {
        setError('Date of birth is required');
        setIsSubmitting(false);
        return;
      }

      // Build profile data
      const profileData: BabyProfileData = {
        name: name.trim(),
        profileType: 'baby',
        dateOfBirth: dateOfBirth || undefined,
        gender: gender || undefined,
        bloodType: bloodType || undefined,
        allergies: allergies ? allergies.split(',').map(a => a.trim()).filter(Boolean) : undefined,
        medications: medications ? medications.split(',').map(m => m.trim()).filter(Boolean) : undefined,
        notes: notes || undefined,
        dailyFeedTargetMl: dailyFeedTargetMinMl ? parseInt(dailyFeedTargetMinMl) : undefined,
        dailyFeedTargetMinMl: dailyFeedTargetMinMl ? parseInt(dailyFeedTargetMinMl) : undefined,
        dailyFeedTargetMaxMl: dailyFeedTargetMaxMl ? parseInt(dailyFeedTargetMaxMl) : undefined,
        postFeedUprightMinutes: postFeedUprightMinutes ? parseInt(postFeedUprightMinutes) : undefined,
        pediatrician: (pedName || pedPhone || pedAddress) ? {
          name: pedName,
          phone: pedPhone || undefined,
          address: pedAddress || undefined,
        } : undefined,
      };

      if (isNew) {
        // Generate new AES key for this baby
        const aesKey = await generateAESKey();

        // Encrypt the profile data
        const encryptedData = await encryptBabyProfile(profileData, aesKey);

        // Import user's public key and encrypt the AES key with it
        const publicKey = await importPublicKey(user!.publicKey);
        const encryptedBabyKey = await encryptKeyWithPublicKey(aesKey, publicKey);

        // Create baby on server
        const result = await createMutation.mutateAsync({
          encryptedData,
          encryptedBabyKey,
          profileType: 'BABY',
        });

        // Store the AES key locally for future access
        await storeBabyKey(result.id, encryptedBabyKey);
      } else {
        // Get existing key
        const aesKey = await getBabyKey(babyId!);
        if (!aesKey) {
          setError('Could not find encryption key');
          setIsSubmitting(false);
          return;
        }

        // Encrypt updated data
        const encryptedData = await encryptBabyProfile(profileData, aesKey);

        // Update on server
        await updateMutation.mutateAsync({
          id: babyId!,
          encryptedData,
        });
      }

      setIsSubmitting(false);

      if (!isNew) {
        // Show success feedback for updates
        alert('Profile updated successfully!');
      }
    } catch (err) {
      console.error('Failed to save baby profile:', err);
      setError((err as Error).message || 'Failed to save profile');
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!babyId || isNew) return;

    try {
      await deleteMutation.mutateAsync(babyId);
    } catch (err) {
      setError((err as Error).message || 'Failed to delete profile');
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
          <div className="space-y-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 dark:bg-gray-700 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-2"
        >
          &larr; Back
        </button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {isNew ? 'Add Baby Profile' : 'Edit Baby Profile'}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          {isNew
            ? 'Create a new profile for your little one'
            : 'Update your baby\'s information'}
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Profile Photo Card */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Profile Photo
          </h2>

          <div className="flex items-center gap-6">
            {/* Photo Preview */}
            <div className="relative">
              {profilePhotoUrl ? (
                <img
                  src={profilePhotoUrl}
                  alt="Baby profile"
                  className="w-24 h-24 rounded-full object-cover border-4 border-primary-100 dark:border-primary-900"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-800 dark:to-primary-900 flex items-center justify-center">
                  <span className="text-4xl">
                    {name ? name.charAt(0).toUpperCase() : '?'}
                  </span>
                </div>
              )}
              {isUploadingPhoto && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
                  <div className="spinner" />
                </div>
              )}
            </div>

            {/* Upload Controls */}
            <div className="flex-1">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                {isNew
                  ? 'Save the profile first, then you can add a photo'
                  : 'Upload a photo for your baby\'s profile. The image will be encrypted and stored securely.'}
              </p>
              {!isNew && (
                <div className="flex gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    id="photo-upload"
                  />
                  <label
                    htmlFor="photo-upload"
                    className={`btn-primary btn-sm cursor-pointer ${isUploadingPhoto ? 'opacity-50 pointer-events-none' : ''}`}
                  >
                    {isUploadingPhoto ? 'Uploading...' : profilePhotoUrl ? 'Change Photo' : 'Upload Photo'}
                  </label>
                  {profilePhotoUrl && (
                    <button
                      type="button"
                      onClick={handleRemovePhoto}
                      className="btn-secondary btn-sm"
                    >
                      Remove
                    </button>
                  )}
                </div>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                Max 5MB. JPG, PNG, or GIF.
              </p>
            </div>
          </div>
        </div>

        {/* Basic Info Card */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Basic Information
          </h2>

          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Baby's Name *
              </label>
              <input
                id="name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input"
                placeholder="Enter baby's name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="dob" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Date of Birth *
                </label>
                <input
                  id="dob"
                  type="date"
                  required
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="input"
                />
              </div>
              <div>
                <label htmlFor="gender" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Gender
                </label>
                <select
                  id="gender"
                  value={gender}
                  onChange={(e) => setGender(e.target.value as 'male' | 'female' | 'other' | '')}
                  className="input"
                >
                  <option value="">Select...</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="bloodType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Blood Type
              </label>
              <select
                id="bloodType"
                value={bloodType}
                onChange={(e) => setBloodType(e.target.value)}
                className="input"
              >
                <option value="">Unknown</option>
                <option value="A+">A+</option>
                <option value="A-">A-</option>
                <option value="B+">B+</option>
                <option value="B-">B-</option>
                <option value="AB+">AB+</option>
                <option value="AB-">AB-</option>
                <option value="O+">O+</option>
                <option value="O-">O-</option>
              </select>
            </div>
          </div>
        </div>

        {/* Medical Info Card */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Medical Information
          </h2>

          <div className="space-y-4">
            <div>
              <label htmlFor="allergies" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Allergies
              </label>
              <input
                id="allergies"
                type="text"
                value={allergies}
                onChange={(e) => setAllergies(e.target.value)}
                className="input"
                placeholder="Separate with commas (e.g., Dairy, Peanuts)"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Separate multiple allergies with commas
              </p>
            </div>

            <div>
              <label htmlFor="medications" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Current Medications
              </label>
              <input
                id="medications"
                type="text"
                value={medications}
                onChange={(e) => setMedications(e.target.value)}
                className="input"
                placeholder="Separate with commas"
              />
            </div>
          </div>
        </div>

        {/* Daily Feed Target Card */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Daily Feed Target
          </h2>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="feedTargetMin" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Minimum (ml)
                </label>
                <input
                  id="feedTargetMin"
                  type="number"
                  min="0"
                  max="5000"
                  value={dailyFeedTargetMinMl}
                  onChange={(e) => setDailyFeedTargetMinMl(e.target.value)}
                  className="input"
                  placeholder="e.g., 480"
                />
              </div>
              <div>
                <label htmlFor="feedTargetMax" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Maximum (ml)
                </label>
                <input
                  id="feedTargetMax"
                  type="number"
                  min="0"
                  max="5000"
                  value={dailyFeedTargetMaxMl}
                  onChange={(e) => setDailyFeedTargetMaxMl(e.target.value)}
                  className="input"
                  placeholder="e.g., 600"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              {(dailyFeedTargetMinMl || dailyFeedTargetMaxMl) && (
                <button
                  type="button"
                  onClick={() => { setDailyFeedTargetMinMl(''); setDailyFeedTargetMaxMl(''); }}
                  className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  Clear
                </button>
              )}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Set a daily feed target range to track progress on the dashboard. The bar turns green when you reach the minimum and amber past the maximum.
            </p>

            <div>
              <button
                type="button"
                onClick={() => setShowWeightCalculator(!showWeightCalculator)}
                className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
              >
                {showWeightCalculator ? 'Hide' : 'Show'} weight-based calculator
              </button>
            </div>

            {showWeightCalculator && (
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="mb-3">
                  <label htmlFor="babyWeight" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Baby's Weight (kg)
                  </label>
                  <input
                    id="babyWeight"
                    type="number"
                    min="0"
                    max="30"
                    step="0.1"
                    value={babyWeightKg}
                    onChange={(e) => setBabyWeightKg(e.target.value)}
                    className="input"
                    placeholder="e.g., 4.5"
                  />
                </div>

                {babyWeightKg && parseFloat(babyWeightKg) > 0 && (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Estimated range:{' '}
                      <span className="font-semibold">
                        {Math.round(parseFloat(babyWeightKg) * 120)}ml
                      </span>{' '}
                      to{' '}
                      <span className="font-semibold">
                        {Math.round(parseFloat(babyWeightKg) * 150)}ml
                      </span>{' '}
                      per day
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setDailyFeedTargetMinMl(String(Math.round(parseFloat(babyWeightKg) * 120)));
                        setDailyFeedTargetMaxMl(String(Math.round(parseFloat(babyWeightKg) * 150)));
                      }}
                      className="btn-secondary btn-sm"
                    >
                      Use range ({Math.round(parseFloat(babyWeightKg) * 120)}–{Math.round(parseFloat(babyWeightKg) * 150)}ml)
                    </button>
                  </div>
                )}

                <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                  This estimate is based on a general guideline of 120-150ml per kg of body weight per day.
                  Every baby is different. Always consult your pediatrician or health visitor for personalised
                  feeding advice. This is not medical advice.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Post-Feed Upright Timer Card */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Post-Feed Upright Timer
          </h2>

          <div className="space-y-4">
            <div>
              <label htmlFor="uprightMinutes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Duration (minutes)
              </label>
              <div className="flex gap-2">
                <input
                  id="uprightMinutes"
                  type="number"
                  min="1"
                  max="120"
                  value={postFeedUprightMinutes}
                  onChange={(e) => setPostFeedUprightMinutes(e.target.value)}
                  className="input flex-1"
                  placeholder="e.g., 30"
                />
                {postFeedUprightMinutes && (
                  <button
                    type="button"
                    onClick={() => setPostFeedUprightMinutes('')}
                    className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 px-2"
                  >
                    Clear
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Automatically offer to start a countdown timer after logging a feed. Helps track how long to keep baby upright to reduce spit-up.
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                Leave empty to disable the post-feed timer prompt
              </p>
            </div>
          </div>
        </div>

        {/* Pediatrician Card */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Pediatrician
          </h2>

          <div className="space-y-4">
            <div>
              <label htmlFor="pedName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Doctor's Name
              </label>
              <input
                id="pedName"
                type="text"
                value={pedName}
                onChange={(e) => setPedName(e.target.value)}
                className="input"
                placeholder="Dr. Smith"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="pedPhone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Phone
                </label>
                <input
                  id="pedPhone"
                  type="tel"
                  value={pedPhone}
                  onChange={(e) => setPedPhone(e.target.value)}
                  className="input"
                  placeholder="+44 123 456 7890"
                />
              </div>

              <div>
                <label htmlFor="pedAddress" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Address/Clinic
                </label>
                <input
                  id="pedAddress"
                  type="text"
                  value={pedAddress}
                  onChange={(e) => setPedAddress(e.target.value)}
                  className="input"
                  placeholder="Medical Centre, London"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Notes Card */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Notes
          </h2>

          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="input min-h-[100px]"
            placeholder="Any additional notes about your baby..."
          />
        </div>

        {/* Caregivers Section - Only show for existing profiles */}
        {!isNew && babyData?.role === 'PRIMARY' && (
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Caregivers
              </h2>
              <button
                type="button"
                onClick={() => setShowInviteForm(!showInviteForm)}
                className="btn-primary btn-sm"
              >
                + Invite Caregiver
              </button>
            </div>

            {/* Invite Form */}
            {showInviteForm && (
              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                <h3 className="font-medium text-gray-900 dark:text-white mb-3">
                  Invite a Caregiver
                </h3>

                {inviteLink ? (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Share this link with the caregiver:
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        readOnly
                        value={inviteLink}
                        className="input flex-1 text-sm bg-white dark:bg-gray-700"
                      />
                      <button
                        type="button"
                        onClick={copyInviteLink}
                        className="btn-secondary btn-sm"
                      >
                        Copy
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      This link expires in 7 days
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setInviteLink(null);
                        setShowInviteForm(false);
                      }}
                      className="text-sm text-primary-600 hover:underline"
                    >
                      Done
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                        Email (optional)
                      </label>
                      <input
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="caregiver@example.com"
                        className="input"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        If provided, only this email can accept the invite
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                        Role
                      </label>
                      <select
                        value={inviteRole}
                        onChange={(e) => setInviteRole(e.target.value as 'MEMBER')}
                        className="input"
                      >
                        <option value="MEMBER">Caregiver (can log events)</option>
                      </select>
                    </div>

                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleCreateInvite}
                        disabled={isInviting}
                        className="btn-primary btn-sm"
                      >
                        {isInviting ? 'Creating...' : 'Create Invite Link'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowInviteForm(false)}
                        className="btn-secondary btn-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Current Members */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Current Access
              </h3>

              {members && members.length > 0 ? (
                <div className="space-y-2">
                  {members.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {member.name}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {member.email} • {member.role.toLowerCase()}
                        </p>
                      </div>
                      {member.role !== 'PRIMARY' && (
                        <button
                          type="button"
                          onClick={() => {
                            if (confirm(`Remove ${member.name}'s access?`)) {
                              removeMemberMutation.mutate({ odId: babyId!, userId: member.id });
                            }
                          }}
                          className="text-red-600 hover:text-red-700 text-sm"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Only you have access to this profile
                </p>
              )}
            </div>

            {/* Pending Invites */}
            {invites && invites.filter(i => !i.usedAt).length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Pending Invites
                </h3>
                <div className="space-y-2">
                  {invites.filter(i => !i.usedAt).map((invite) => (
                    <div
                      key={invite.id}
                      className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg"
                    >
                      <div>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {invite.email || 'Anyone with link'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {invite.role.toLowerCase()} • expires {new Date(invite.expiresAt).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => deleteInviteMutation.mutate(invite.id)}
                        className="text-red-600 hover:text-red-700 text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Emergency Contacts Section - Only show for existing profiles */}
        {!isNew && babyData?.role === 'PRIMARY' && (
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Emergency Contacts
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Designated contacts who can request emergency access to medical information
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowEmergencyForm(!showEmergencyForm)}
                className="btn-primary btn-sm"
              >
                + Add Contact
              </button>
            </div>

            {/* Add Emergency Contact Form */}
            {showEmergencyForm && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                <h3 className="font-medium text-gray-900 dark:text-white mb-3">
                  Add Emergency Contact
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                      Contact Name *
                    </label>
                    <input
                      type="text"
                      value={emergencyName}
                      onChange={(e) => setEmergencyName(e.target.value)}
                      placeholder="Grandma Mary"
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={emergencyEmail}
                      onChange={(e) => setEmergencyEmail(e.target.value)}
                      placeholder="grandma@example.com"
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                      Relationship *
                    </label>
                    <input
                      type="text"
                      value={emergencyRelationship}
                      onChange={(e) => setEmergencyRelationship(e.target.value)}
                      placeholder="Grandmother"
                      className="input"
                    />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    This person will be able to request emergency access to your baby's medical information (allergies, medications, blood type, pediatrician).
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleAddEmergencyContact}
                      disabled={isAddingEmergency}
                      className="btn-danger btn-sm"
                    >
                      {isAddingEmergency ? 'Adding...' : 'Add Emergency Contact'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowEmergencyForm(false)}
                      className="btn-secondary btn-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Emergency Contacts List */}
            {emergencyContacts && emergencyContacts.length > 0 ? (
              <div className="space-y-3">
                {emergencyContacts.map((contact) => {
                  const pendingRequest = contact.requests?.find(r => r.status === 'PENDING');
                  return (
                    <div
                      key={contact.id}
                      className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {contact.name}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {contact.email} • {contact.relationship}
                          </p>
                          {!contact.isActive && (
                            <span className="inline-block mt-1 px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded text-xs">
                              Inactive
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            if (confirm(`Remove ${contact.name} as an emergency contact?`)) {
                              removeEmergencyContactMutation.mutate(contact.id);
                            }
                          }}
                          className="text-red-600 hover:text-red-700 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                      {/* Pending Request Alert */}
                      {pendingRequest && (
                        <div className="mt-3 p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                          <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                            Pending Emergency Access Request
                          </p>
                          <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                            Requested at {format(new Date(pendingRequest.createdAt), 'PPp')}
                          </p>
                          <div className="flex gap-2 mt-2">
                            <button
                              type="button"
                              onClick={() => approveEmergencyRequestMutation.mutate(pendingRequest.id)}
                              className="btn-primary btn-sm"
                            >
                              Approve
                            </button>
                            <button
                              type="button"
                              onClick={() => denyEmergencyRequestMutation.mutate(pendingRequest.id)}
                              className="btn-secondary btn-sm"
                            >
                              Deny
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No emergency contacts configured. Add trusted contacts who may need access to medical information in emergencies.
              </p>
            )}
          </div>
        )}

        {/* Share Links Section - Only show for existing profiles */}
        {!isNew && babyData?.role === 'PRIMARY' && (
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Share Links
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Create temporary read-only links to share with pediatricians or family
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowShareForm(!showShareForm)}
                className="btn-primary btn-sm"
              >
                + Create Share Link
              </button>
            </div>

            {/* Create Share Link Form */}
            {showShareForm && (
              <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <h3 className="font-medium text-gray-900 dark:text-white mb-3">
                  Create Share Link
                </h3>

                {shareLink ? (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Share this link (it's read-only and will expire):
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        readOnly
                        value={shareLink}
                        className="input flex-1 text-sm bg-white dark:bg-gray-700"
                      />
                      <button
                        type="button"
                        onClick={copyShareLink}
                        className="btn-secondary btn-sm"
                      >
                        Copy
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setShareLink(null);
                        setShowShareForm(false);
                        setShareEventTypes([]);
                        setShareRecipientName('');
                        setShareRecipientEmail('');
                      }}
                      className="text-sm text-primary-600 hover:underline"
                    >
                      Done
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                          Recipient Name (optional)
                        </label>
                        <input
                          type="text"
                          value={shareRecipientName}
                          onChange={(e) => setShareRecipientName(e.target.value)}
                          placeholder="Dr. Smith"
                          className="input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                          Recipient Email (optional)
                        </label>
                        <input
                          type="email"
                          value={shareRecipientEmail}
                          onChange={(e) => setShareRecipientEmail(e.target.value)}
                          placeholder="doctor@clinic.com"
                          className="input"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                        Expires In
                      </label>
                      <select
                        value={shareExpiresInDays}
                        onChange={(e) => setShareExpiresInDays(Number(e.target.value))}
                        className="input"
                      >
                        <option value={1}>1 day</option>
                        <option value={3}>3 days</option>
                        <option value={7}>7 days</option>
                        <option value={14}>14 days</option>
                        <option value={30}>30 days</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                        Include Event Types (leave empty for all)
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {['FEED', 'NAPPY', 'SLEEP', 'MEDICATION', 'TEMPERATURE', 'WEIGHT'].map((type) => (
                          <label key={type} className="flex items-center gap-1 text-sm">
                            <input
                              type="checkbox"
                              checked={shareEventTypes.includes(type)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setShareEventTypes([...shareEventTypes, type]);
                                } else {
                                  setShareEventTypes(shareEventTypes.filter(t => t !== type));
                                }
                              }}
                              className="rounded"
                            />
                            {type.charAt(0) + type.slice(1).toLowerCase().replace('_', ' ')}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleCreateShareLink}
                        disabled={isCreatingShare}
                        className="btn-primary btn-sm"
                      >
                        {isCreatingShare ? 'Creating...' : 'Create Share Link'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowShareForm(false)}
                        className="btn-secondary btn-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Active Share Links */}
            {shareLinks && shareLinks.filter(s => !s.revoked).length > 0 ? (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Active Links
                </h3>
                {shareLinks.filter(s => !s.revoked).map((share) => (
                  <div
                    key={share.id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {share.recipientName || share.recipientEmail || 'Anyone with link'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {share.eventTypes ? share.eventTypes.join(', ') : 'All events'} •
                        Expires {format(new Date(share.expiresAt), 'PP')} •
                        {share.accessCount} view{share.accessCount !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (confirm('Revoke this share link? It will no longer be accessible.')) {
                          revokeShareMutation.mutate(share.id);
                        }
                      }}
                      className="text-red-600 hover:text-red-700 text-sm"
                    >
                      Revoke
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No active share links. Create a link to share baby data with healthcare providers or family.
              </p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary flex-1"
          >
            {isSubmitting ? (
              <>
                <span className="spinner mr-2" />
                {isNew ? 'Creating...' : 'Saving...'}
              </>
            ) : (
              isNew ? 'Create Profile' : 'Save Changes'
            )}
          </button>

          <button
            type="button"
            onClick={() => navigate(-1)}
            className="btn-secondary"
          >
            Cancel
          </button>
        </div>

        {/* Delete button for existing profiles */}
        {!isNew && babyData?.role === 'PRIMARY' && (
          <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-2">
              Danger Zone
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Deleting this profile will permanently remove all data including events, photos, and shared access. This cannot be undone.
            </p>

            {!showDeleteConfirm ? (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="btn-danger"
              >
                Delete Profile
              </button>
            ) : (
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                  className="btn-danger"
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Yes, Delete Forever'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}
      </form>

      {/* Encryption notice */}
      <div className="mt-8 text-center text-xs text-gray-500 dark:text-gray-500">
        <p>All data is encrypted end-to-end on your device</p>
      </div>
    </div>
  );
}
